import { useThrottledCallback } from '@timelyapp/react';
import classNames from 'classnames';
import { EMPTY_ARRAY } from 'constants/defaults';
import React, { useCallback, useMemo } from 'react';
import { useDrop } from 'react-dnd';
import { useSelector } from 'react-redux';
import { createHoursForUserSelector } from 'redux/selectors/hours';
import type { RawCapacity } from 'types/summaries';
import { fromISO } from 'utils/luxon-helpers';
import styles from './GanttChart.module.scss';
import GanttChartAvailabilityLayer from './GanttChartAvailabilityLayer';
import GanttChartDragToCreateLayer from './GanttChartDragToCreateLayer';
import GanttChartTaskDeferred from './GanttChartTaskDeferred';
import type { GanttTaskDragObject, GanttTaskDropResult } from './gantt-dnd';
import { GANTTT_TASK_DND_TYPE } from './gantt-dnd';
import type { GanttLayoutMeta, GanttRowLayout, HeaderDatePreview } from './gantt-internal-types';
import { horizontalOverlap } from './gantt-layout';
import type {
  GanttRow,
  GanttTask,
  GanttTaskCopyParameters,
  GanttTooltipComponent,
} from './gantt-types';

interface Props {
  layout: GanttRowLayout;
  ganttRow: GanttRow;
  height: number;
  showMouseOverPreview: boolean;
  onSetPreviewDates: (preview: HeaderDatePreview | null) => void;
  onMoveTask: (
    task: GanttTask,
    row: GanttRow,
    toDates: { startDate: string; endDate: string },
    meta: { interaction: 'move' | 'resize' }
  ) => void;
  onReassignTask: (task: GanttTask, from: GanttRow, to: GanttRow) => void;
  onCreateTask: (row: GanttRow, parameters: { startDate: string; endDate: string }) => void;

  onClickTask: (task: GanttTask, row: GanttRow) => void;
  onCopyTask: (task: GanttTask, parameters: GanttTaskCopyParameters) => void;
  scrollLeft: number;
  layoutMeta: GanttLayoutMeta;
  scrollAreaWidth: number;
  todayLine: React.ReactNode;
  capacities: Array<RawCapacity>;
  tooltip: GanttTooltipComponent;
  disableTooltip: boolean;
  allowCreateTask: boolean;
  allowChangeTask: boolean;
  showAvailability: boolean;
  defaultWeekDays: Array<string>; // MON, TUE, WED, THU, FRI, SAT, SUN
  weekStartsOn: 'monday' | 'sunday';
}

const GanttChartRow: React.FC<Props> = ({
  allowChangeTask,
  allowCreateTask,
  capacities,
  disableTooltip,
  ganttRow,
  height,
  layout,
  layoutMeta,
  onClickTask,
  onCopyTask,
  onCreateTask,
  onMoveTask,
  onReassignTask,
  onSetPreviewDates,
  scrollAreaWidth,
  scrollLeft,
  showAvailability,
  showMouseOverPreview,
  todayLine,
  tooltip,
  defaultWeekDays,
  weekStartsOn,
}) => {
  const userId = 'userId' in ganttRow ? ganttRow.userId : null;
  const hoursSelector = useMemo(
    () =>
      ganttRow.type === 'user' && userId !== null ? createHoursForUserSelector(userId) : () => [],
    [ganttRow.type, userId]
  );
  const hours = useSelector(hoursSelector);
  const pxToDays = useCallback(
    (deltaPx: number) => Math.round(deltaPx / layoutMeta.zoom.columnWidth),
    [layoutMeta.zoom.columnWidth]
  );
  const applyTaskMove = useCallback(
    (task: { startDate: string; endDate: string }, deltaPx: number) => {
      const dayDelta = pxToDays(deltaPx);
      const startDate = fromISO(task.startDate).plus({ days: dayDelta }).toISODate();
      const endDate = fromISO(task.endDate).plus({ days: dayDelta }).toISODate();
      return { startDate, endDate };
    },
    [pxToDays]
  );

  const updatePreviewOnMove = useThrottledCallback((task: GanttTask, deltaPx: number) => {
    onSetPreviewDates({
      ...applyTaskMove(task, deltaPx),
      originalStartDate: task.startDate,
      originalEndDate: task.endDate,
    });
  }, 50);
  const updatePreviewOnResizing = useCallback(
    (task: GanttTask, newDates: { startDate: string; endDate: string }) => {
      onSetPreviewDates({
        ...newDates,
        originalStartDate: task.startDate,
        originalEndDate: task.endDate,
      });
    },
    [onSetPreviewDates]
  );
  const resetPreview = useCallback(() => {
    onSetPreviewDates(null);
    updatePreviewOnMove.cancel();
  }, [onSetPreviewDates, updatePreviewOnMove]);

  const [, drop] = useDrop(
    () => ({
      accept: GANTTT_TASK_DND_TYPE,
      drop(item: GanttTaskDragObject, monitor): Omit<GanttTaskDropResult, 'dropEffect'> {
        const delta =
          monitor.getDifferenceFromInitialOffset()!.x + (scrollLeft - item.initialScrollLeft);
        const move = applyTaskMove(item.task, delta);

        resetPreview();
        return {
          move,
          toRow: ganttRow,
        };
      },
      canDrop: () => {
        if (ganttRow.type === 'project') {
          return !ganttRow.projectArchived;
        }
        return true;
      },
      hover: (item: GanttTaskDragObject, monitor) => {
        const delta =
          monitor.getDifferenceFromInitialOffset()!.x + (scrollLeft - item.initialScrollLeft);
        updatePreviewOnMove(item.task, delta);
      },
    }),
    [updatePreviewOnMove, resetPreview, applyTaskMove, scrollLeft, ganttRow]
  );

  const handleUpdateResizePreview = useCallback(
    (task: GanttTask, newDates: null | { startDate: string; endDate: string }) => {
      if (newDates == null) {
        resetPreview();
      } else {
        updatePreviewOnResizing(task, newDates);
      }
    },
    [resetPreview, updatePreviewOnResizing]
  );

  const handleCreateTask = useCallback(
    (parameters: { startDate: string; endDate: string }) => {
      onCreateTask(ganttRow, parameters);
    },
    [onCreateTask, ganttRow]
  );

  const tasks = useMemo(() => {
    const result = layout
      .getTasks(layoutMeta)
      .filter(task => horizontalOverlap(task, { left: scrollLeft, width: scrollAreaWidth }));
    return result.length > 0 ? result : EMPTY_ARRAY;
  }, [layout, layoutMeta, scrollLeft, scrollAreaWidth]);

  const getHiddenTasks = useMemo(
    () => () => {
      const result = layout
        .getHiddenTasks(layoutMeta)
        .filter(task => horizontalOverlap(task, { left: scrollLeft, width: scrollAreaWidth }));
      return result.length > 0 ? result : EMPTY_ARRAY;
    },
    [layout, layoutMeta, scrollLeft, scrollAreaWidth]
  );
  // add warning cursor if we can't create tasks
  return (
    <div
      ref={drop}
      className={classNames(styles.rowWrapper, {
        [styles.notAllowedCreateTasks]: !allowCreateTask,
      })}
    >
      {todayLine}
      {allowCreateTask && (
        <GanttChartDragToCreateLayer
          ganttRow={ganttRow}
          laneSpecification={layout.laneSpecification}
          height={height}
          layoutMeta={layoutMeta}
          showMouseOverPreview={showMouseOverPreview}
          onUpdateResizePreview={onSetPreviewDates}
          onCreateTask={handleCreateTask}
        />
      )}
      {ganttRow.type === 'user' && showAvailability && (
        <GanttChartAvailabilityLayer
          hours={hours}
          layoutMeta={layoutMeta}
          tasks={tasks}
          hiddenTasks={getHiddenTasks()}
          capacities={capacities}
          scrollLeft={scrollLeft}
          scrollAreaWidth={scrollAreaWidth}
          isUnassigned={ganttRow.userId == null}
          zoomView={layoutMeta.zoom.view}
          defaultWeekDays={defaultWeekDays}
          weekStartsOn={weekStartsOn}
        />
      )}
      {tasks.map(task => (
        <GanttChartTaskDeferred
          key={task.task.id}
          ganttRow={ganttRow}
          taskWithLayout={task}
          layoutMeta={layoutMeta}
          scrollLeft={scrollLeft}
          scrollAreaWidth={scrollAreaWidth}
          onDragStop={resetPreview}
          onUpdateResizePreview={handleUpdateResizePreview}
          onClickTask={onClickTask}
          onMoveTask={onMoveTask}
          onReassignTask={onReassignTask}
          onCopyTask={onCopyTask}
          tooltip={tooltip}
          disableTooltip={disableTooltip}
          allowChangeTask={allowChangeTask}
        />
      ))}
    </div>
  );
};

export default GanttChartRow;
